"use client";

import React, { useId, useMemo } from "react";
import { EyeIcon, EyeOffIcon, CheckIcon, XIcon, Lock } from "lucide-react";
import zxcvbn from "zxcvbn";
import { Input } from "@/components/ui/input";

type Props = {
	value: string;
	onChange: (v: string) => void;
};

export default function PasswordStrengthInput({ value, onChange }: Props) {
	const id = useId();
	const [isVisible, setIsVisible] = React.useState(false);
	const toggle = () => setIsVisible((p) => !p);

	/* Calcola la forza della password */
	const result = useMemo(() => zxcvbn(value), [value]);
	const score = result.score; // 0-4

	const requirements = [
		{ met: /.{8,}/.test(value), text: "Almeno 8 caratteri" },
		{ met: /[0-9]/.test(value), text: "Almeno 1 numero" },
		{ met: /[a-z]/.test(value), text: "Almeno 1 minuscola" },
		{ met: /[A-Z]/.test(value), text: "Almeno 1 maiuscola" },
	];

	const barColor = [
		"bg-border",
		"bg-red-500",
		"bg-orange-500",
		"bg-amber-500",
		"bg-emerald-500",
	][score];

	const strengthText = [
		"Inserisci una password",
		"Password debole",
		"Password debole",
		"Password media",
		"Password forte",
	][score];

	return (
		<div>
			{/* Input + Icona toggle */}
			<div className="relative mt-2">
				<Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-(--card-foreground) w-4 h-4" />
				<Input
					id={id}
					name="new-password"
					autoComplete="new-password"
					type={isVisible ? "text" : "password"}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					className="pl-10 pr-10 text-(--foreground)"
					placeholder="Password"
				/>
				<button
					type="button"
					onClick={toggle}
					className="absolute inset-y-0 end-0 flex w-9 items-center justify-center text-muted-foreground"
					aria-label={isVisible ? "Nascondi password" : "Mostra password"}
					tabIndex={-1}
				>
					{isVisible ? (
						<EyeOffIcon
							size={16}
							className="cursor-pointer text-(--card-foreground)"
						/>
					) : (
						<EyeIcon
							size={16}
							className="cursor-pointer text-(--card-foreground)"
						/>
					)}
				</button>
			</div>

			{/* Barra di forza della password */}
			<div className="bg-border my-3 h-1 w-full rounded-full overflow-hidden">
				<div
					className={`h-full rounded ${barColor} transition-all duration-500 ease-in-out`}
					style={{
						width: `${(score / 4) * 100}%`,
					}}
				/>
			</div>

			<p className="mb-1 text-sm font-medium text-(--foreground)">
				{strengthText}. Deve contenere:
			</p>

			{/* Lista requisiti */}
			<ul className="space-y-1.5 text-xs">
				{requirements.map((r, i) => (
					<li key={i} className="flex items-center gap-2">
						{r.met ? (
							<CheckIcon size={14} className="text-emerald-500" />
						) : (
							<XIcon size={14} />
						)}
						<span
							className={r.met ? "text-emerald-600" : "text-muted-foreground"}
						>
							{r.text}
						</span>
					</li>
				))}
			</ul>
		</div>
	);
}
